import * as React from "react";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useEffect, useRef, useState, useCallback } from "react";
import { retrieveGymMember as retrieveMemberDetail } from "../../pages/Apihelper/MemberDashboard";

const MIN_WIDTH = 300;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 380;
const STORAGE_KEY = "memberQuickViewWidth";
const HEADER_HEIGHT = 64;

const readWidth = () => {
  try {
    const n = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(n) && n >= MIN_WIDTH ? Math.min(n, MAX_WIDTH) : DEFAULT_WIDTH;
  } catch (e) {
    return DEFAULT_WIDTH;
  }
};

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

function Row({ label, children }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{children}</Typography>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>{title}</Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

function daysChip(daysLeft, expired, expiredDays) {
  if (String(expired).toLowerCase() === "yes" || expired === true) {
    return <Chip size="small" color="error" label={expiredDays ? `Expired ${expiredDays} day(s) ago` : "Expired"} />;
  }
  const n = Number(daysLeft);
  if (!Number.isFinite(n)) return null;
  return <Chip size="small" color={n <= 7 ? "warning" : "success"} label={`${n} day(s) left`} />;
}

/**
 * A slide-in panel with the few things staff usually need about a member, so they don't have to
 * open the full details page. The left edge can be dragged to make it wider or narrower (the width
 * is remembered). The page keeps working beside it: other rows can be clicked to switch member.
 *
 * Props: member (list row, or null = closed), onClose, onOpenFull(id), onWidthChange(px used on the
 * right of the page; 0 when closed or full-screen on a phone)
 */
export default function MemberQuickView({ member, onClose, onOpenFull, onWidthChange }) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [width, setWidth] = useState(readWidth);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shown, setShown] = useState(null); // keeps the content while the panel slides out
  const widthRef = useRef(width);
  widthRef.current = width;

  const open = Boolean(member);
  useEffect(() => { if (member) setShown(member); }, [member]);

  // Tell the page how much room the panel takes
  useEffect(() => {
    onWidthChange?.(open && !isMobile ? width : 0);
  }, [open, isMobile, width, onWidthChange]);
  useEffect(() => () => onWidthChange?.(0), []); // eslint-disable-line

  // Load the member's details whenever a different member is opened
  useEffect(() => {
    if (!member?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    (async () => {
      try {
        const userDataObj = JSON.parse(localStorage.getItem("userdata"));
        const res = await retrieveMemberDetail(userDataObj.token, member.id, userDataObj.id);
        if (!cancelled && (res.code == "200" || res.code === 200)) setDetail(res.data);
      } catch (e) {
        /* the list row still gives the basics */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [member?.id]);

  const startDrag = useCallback((event) => {
    event.preventDefault();
    const previousSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    const move = (e) => {
      const next = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth * 0.8, window.innerWidth - e.clientX));
      setWidth(next);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.userSelect = previousSelect;
      try { localStorage.setItem(STORAGE_KEY, String(Math.round(widthRef.current))); } catch (e) { /* not saved */ }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, []);

  const m = { ...(shown || {}), ...(detail || {}) };
  const pay = detail?.payments && !Array.isArray(detail.payments) ? detail.payments : null;
  const yearly = detail?.yearly_package_payments && !Array.isArray(detail.yearly_package_payments) ? detail.yearly_package_payments : null;
  const pt = detail?.pt_data && !Array.isArray(detail.pt_data) ? detail.pt_data : null;
  const active = Number(m.status) === 1;

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        square
        sx={{
          position: "fixed",
          right: 0,
          top: HEADER_HEIGHT,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          width: isMobile ? "100%" : width,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          display: "flex",
          flexDirection: "column",
          borderLeft: "1px solid",
          borderColor: "divider",
        }}
      >
        {!isMobile && (
          <Box
            onPointerDown={startDrag}
            role="separator"
            aria-label="Drag to resize"
            title="Drag to resize"
            sx={{
              position: "absolute", left: -4, top: 0, bottom: 0, width: 10, cursor: "col-resize", zIndex: 2,
              "&:hover > div, &:active > div": { opacity: 1 },
            }}
          >
            <Box sx={{ position: "absolute", left: 4, top: "50%", transform: "translateY(-50%)", width: 4, height: 48, borderRadius: 2, bgcolor: "primary.main", opacity: 0.35, transition: "opacity .15s" }} />
          </Box>
        )}

        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Avatar src={m.image} alt={m.name} sx={{ width: 56, height: 56 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{m.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">{m.membership_number}</Typography>
            <Box sx={{ mt: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              <Chip size="small" color={active ? "success" : "error"} label={active ? "Active" : "Inactive"} />
              {m.pause ? <Chip size="small" color="warning" label="Paused" /> : null}
            </Box>
          </Box>
          <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
          <Section title="Contact">
            <Row label="Phone">{m.phone}</Row>
            <Row label="Alternate">{m.alter_phone || m.alternate_phone}</Row>
            <Row label="Email">{m.email}</Row>
            <Row label="Card">{m.card_number && m.card_number !== "0" ? m.card_number : null}</Row>
          </Section>
          <Divider sx={{ mb: 2 }} />

          {loading && (
            <>
              <Skeleton height={28} /><Skeleton height={28} /><Skeleton height={28} />
            </>
          )}

          {!loading && (
            <>
              <Section title="Membership">
                {pay ? (
                  <>
                    <Row label="Package">{pay.member_package}</Row>
                    <Row label="Valid">{pay.start_date && pay.end_date ? `${pay.start_date} → ${pay.end_date}` : null}</Row>
                    <Box sx={{ py: 0.5 }}>{daysChip(pay.days_left, pay.expired, pay.expired_days)}</Box>
                    {Number(pay.due) > 0 && <Row label="Due">{money(pay.due)}</Row>}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">No package yet</Typography>
                )}
              </Section>

              {yearly && (
                <Section title="Yearly membership">
                  <Row label="Valid till">{yearly.end_date}</Row>
                  {Number(yearly.due) > 0 && <Row label="Due">{money(yearly.due)}</Row>}
                </Section>
              )}

              {pt && (
                <Section title="Personal training">
                  <Row label="Plan">{pt.trainer_package}</Row>
                  <Row label="Trainer">{pt.trainer_name}</Row>
                  <Row label="Valid till">{pt.end_date}</Row>
                  <Box sx={{ py: 0.5 }}>{daysChip(pt.days_left, pt.expired, pt.expired_days)}</Box>
                  {Number(pt.due) > 0 && <Row label="Due">{money(pt.due)}</Row>}
                </Section>
              )}

              {detail?.bath_available === "Yes" && (
                <Section title="Steam bath">
                  <Row label="Remaining">{detail.remaining_bath}</Row>
                </Section>
              )}
            </>
          )}
        </Box>

        <Box sx={{ p: 2, display: "flex", gap: 1, borderTop: "1px solid", borderColor: "divider" }}>
          {m.phone ? (
            <Button component="a" href={`tel:${m.phone}`} variant="outlined" startIcon={<PhoneIcon />} sx={{ textTransform: "none", fontWeight: 600 }}>
              Call
            </Button>
          ) : null}
          <Button fullWidth variant="contained" endIcon={<OpenInNewIcon />} onClick={() => onOpenFull?.(m.id)} sx={{ textTransform: "none", fontWeight: 600 }}>
            Full details
          </Button>
        </Box>
      </Paper>
    </Slide>
  );
}
